/**
 * Simbolo de tipo de energia, recortado de la carta de energia basica real.
 *
 * Historia de esto, porque el camino importa: primero se recorto la carta con
 * `background-size`, y a 15 px salian manchas. Se sustituyo por siluetas SVG
 * dibujadas a mano, y salieron mal -- la llama parecia una gota, el puño no era
 * un puño y el dragon no parecia nada. Dibujar de memoria los simbolos de un
 * juego que la gente conoce de vista no funciona.
 *
 * La solucion es usar la imagen real, pero recortandola BIEN: en una carta de
 * energia basica el simbolo es negro, esta centrado horizontalmente y su centro
 * cae a un 45 % de la altura, ocupando ~38 % del ancho. Con una imagen absoluta
 * ampliada dentro de un contenedor circular con `overflow: hidden` se controla
 * eso con exactitud, cosa que `background-position` en porcentajes no permite
 * (posiciona proporcionalmente, no respecto a un punto concreto).
 *
 * Los tipos SIN carta de energia basica (Incolora, Hada) no tienen de donde
 * recortar: para esos queda el disco de color con la inicial, que al menos es
 * honesto en vez de un simbolo inventado. Dragon SI tiene el suyo, pero como
 * imagen propia en /tipos/dragon.webp -- ver mas abajo.
 */

/**
 * De donde sale el simbolo de cada tipo, y como recortarlo.
 *
 * Los ocho tipos con energia basica salen de la expansion SVE, todos con el
 * mismo encuadre porque comparten diseño de carta.
 *
 * Incolora y Dragon NO tienen energia basica, pero su simbolo si existe: manda
 * el arte de las energias especiales "Double Colorless" y "Double Dragon". Son
 * cartas de diseño antiguo, con el simbolo en otra posicion y a otro tamaño, asi
 * que llevan su propio encuadre en vez del comun.
 *
 * (Se probo antes si TCGdex servia simbolos sueltos en /univ/energy/{tipo}: esa
 * ruta devuelve 200 para CUALQUIER texto, incluido uno inventado. Es una pagina
 * de error, no un asset.)
 */
/**
 * ENCUADRE MEDIDO, NO ESTIMADO.
 *
 * La primera version puso el centro al 45 % de la altura a ojo y los simbolos
 * salian descuadrados. Estos numeros salen de analizar los pixeles de cada
 * imagen: se cargan en un canvas (por el proxy de la API, que evita que quede
 * "manchado") y se localiza la masa de pixeles oscuros -- el simbolo es negro
 * sobre un fondo claro de color -- descartando el 5 % extremo de cada eje para
 * que un pixel suelto del fondo no arruine la medida.
 *
 * Resultado: en las energias basicas el centro real esta al ~53 % de la altura,
 * no al 45 %. `zoom` se deriva del tamaño medido de cada simbolo para que todos
 * ocupen aproximadamente lo mismo dentro del disco, pese a que unos son anchos
 * (el ojo Psiquico) y otros estrechos (el rayo).
 */
const SYMBOLS = {
  Grass:     { url: 'https://images.pokemontcg.io/sve/1_hires.png', cy: 52.1, zoom: 275 },
  Fire:      { url: 'https://images.pokemontcg.io/sve/2_hires.png', cy: 52.9, zoom: 235 },
  Water:     { url: 'https://images.pokemontcg.io/sve/3_hires.png', cx: 47.7, cy: 55.0, zoom: 300 },
  Lightning: { url: 'https://images.pokemontcg.io/sve/4_hires.png', cy: 52.6, zoom: 335 },
  Psychic:   { url: 'https://images.pokemontcg.io/sve/5_hires.png', cy: 52.8, zoom: 247 },
  Fighting:  { url: 'https://images.pokemontcg.io/sve/6_hires.png', cy: 53.4, zoom: 264 },
  Darkness:  { url: 'https://images.pokemontcg.io/sve/7_hires.png', cy: 53.7, zoom: 274 },
  Metal:     { url: 'https://images.pokemontcg.io/sve/8_hires.png', cy: 53.9, zoom: 260 },
  /**
   * Estas dos son cartas antiguas, con fondo y texto oscuros. Con la ventana de
   * busqueda amplia la deteccion encontraba las LETRAS del titulo en vez del
   * simbolo -- el icono de Dragon llego a mostrar la palabra "Dragon Energy".
   * Se midieron igual que las demas pero acotando la ventana a la zona donde
   * esta el simbolo, lejos del titulo y del cuadro de texto.
   */
  Colorless: {
    url: 'https://assets.tcgdex.net/en/xy/xy12/90/high.webp',
    cx: 49.3, cy: 65.2, zoom: 311,
  },
  /*
   * Dragon NO se recorta de una carta: es el simbolo suelto, servido desde el
   * propio sitio.
   *
   * Recortarlo de "Double Dragon Energy" nunca quedo bien centrado -- es una
   * carta antigua, con el simbolo pequeño y descolocado respecto a las demas, y
   * por mucho que se afinen cx/cy el resultado seguia bailando frente al resto
   * de tipos. Con la imagen del simbolo aislado el encuadre es el trivial: todo
   * el dibujo, centrado.
   *
   * De paso deja de depender de un servidor ajeno para este icono.
   */
  Dragon: {
    url: '/tipos/dragon.webp',
    cx: 50, cy: 50, zoom: 104,
  },
};

/** Valores por defecto; cada tipo sobreescribe lo que le corresponda. */
const DEFAULT_FRAME = { zoom: 270, cx: 50, cy: 53 };

export default function TypeIcon({ type, label, share, size = 18 }) {
  const s = SYMBOLS[type];
  const title = [label ?? type, share != null ? `${Math.round(share * 100)}%` : null]
    .filter(Boolean)
    .join(' · ');

  if (!s) {
    // Tipo sin simbolo conocido: disco neutro con la inicial, que es honesto.
    return (
      <span className="typeicon" title={title} style={{ width: size, height: size }}>
        {/* Sin tipo NI rotulo no hay inicial que sacar, y `.charAt` sobre
            undefined tumbaba la vista entera. Un icono es decoracion: si no
            se sabe que dibujar, se calla -- nunca se lleva la pagina por
            delante. */}
        <span className="typeicon__letter">{(label ?? type ?? '?').charAt(0)}</span>
        <span className="sr-only">{title}</span>
      </span>
    );
  }

  const f = { ...DEFAULT_FRAME, ...s };
  return (
    <span
      className="typeicon"
      title={title}
      style={{ width: size, height: size, '--zoom': `${f.zoom}%`, '--cx': `${f.cx}%`, '--cy': `${f.cy}%` }}
    >
      <img src={s.url} alt="" aria-hidden="true" loading="lazy" />
      <span className="sr-only">{title}</span>
    </span>
  );
}
